import {
  computed, getCurrentInstance, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { loadUserPreferences } from '../utils/user_preferences';
import {
  loadServerLocations, measuredPings, serverLocations, setServerLatency,
} from '../utils/server_locations';
import { trackSharedLoad } from './useSharedLoads';

// What every signed-in page needs, whichever layout wraps it: preferences, game and team
// (with the X-GAME / X-TEAM-ID headers), mods, level-1 notifications, server pings, and the
// matchmaking and presence channels. DashboardLayout and the v5 shell both call this once.

// Module scope: the layout wraps every page and remounts on each route change,
// so a component-scoped guard would re-probe every region on every navigation.
// Only a real page load resets it, which is when the latencies are stale anyway.
//
// Per region rather than one flag, so a region that arrives late -- after a
// failed fetch and a successful retry -- is still probed, and the rest are not
// probed twice. Matchmaking re-probes them all once the sweep is PING_MAX_AGE_MS old.
const PING_MAX_AGE_MS = 5 * 60 * 1000;
const PING_TIMEOUT_MS = 5000;
const latencyProbes = new Map();
let latencyProbedAt = Date.now();
let pingsReportedForPartyId = null;

// Matchmaking status shown on its nav item, so a queue or a live match is
// still visible after you navigate away from the matchmaking page.
const INACTIVE_LOBBY_STATUSES = ['completed', 'cancelled', 'forming'];

async function measureLatency(server) {
  return new Promise((resolve, reject) => {
    let startTime = Date.now();
    let socket = null;
    try {
      socket = new WebSocket(`wss://${server.value}.dathost.net/console-server/`);
    } catch (error) {
      reject(error);
      return;
    }

    function settle(done, value) {
      // eslint-disable-next-line no-use-before-define
      clearTimeout(timer);
      try { socket.close(); } catch { /* already closing */ }
      done(value);
    }

    // A socket that opens and then never answers would otherwise be held
    // open, with its ping unresolved, for as long as the tab is.
    const timer = setTimeout(
      () => settle(reject, new Error(`Timed out pinging ${server.value}`)),
      PING_TIMEOUT_MS,
    );

    socket.onopen = () => {
      startTime = Date.now();
      socket.send(JSON.stringify({ cmd: 'datPing' }));
    };

    socket.onmessage = (event) => {
      let response = null;
      try {
        response = JSON.parse(event.data);
      } catch {
        return; // Not ours. Wait for a datPong or for the timeout.
      }
      // Only a datPong carries a measurement.
      if (response?.cmd !== 'datPong') return;
      settle(resolve, Date.now() - startTime);
    };

    socket.onerror = (err) => settle(reject, err);
  });
}

// Each ping lands on its own, so a blocked or slow region holds up nothing
// and one nobody can reach simply keeps no ms.
function processServersLatency(servers) {
  servers.forEach((server) => {
    if (latencyProbes.has(server.id)) return;

    latencyProbes.set(server.id, measureLatency(server)
      .then((ms) => setServerLatency(server.id, ms))
      .catch((error) => console.log('Could not ping server', server.value, error)));
  });
}

async function freshPings() {
  await loadServerLocations();
  if (Date.now() - latencyProbedAt > PING_MAX_AGE_MS) {
    latencyProbes.clear();
    latencyProbedAt = Date.now();
  }
  processServersLatency(serverLocations.value);
  await Promise.all(latencyProbes.values());

  return measuredPings();
}

function reportPings() {
  freshPings()
    .then((pings) => axios.put('/matchmaking/pings', { pings }))
    .catch(() => {});
}

export default function useAppShell() {
  onMounted(loadUserPreferences);

  const auth = useAuth();
  const store = useStore();
  const { proxy } = getCurrentInstance();
  const activeTeam = ref();
  const selectedGame = ref(store.state.selectedGame);
  const notifications = ref([]);
  const showNotifications = ref(false);

  const activeLobby = computed(() => {
    const lobby = store.getters.matchmakingLobby;
    if (!lobby?.uuid) return null;
    return INACTIVE_LOBBY_STATUSES.includes(lobby.status) ? null : lobby;
  });
  const matchmakingStatus = computed(() => {
    if (activeLobby.value) return 'in_match';
    return store.getters.matchmakingQueueEntry ? 'searching' : null;
  });

  // Probe whatever the catalogue holds, and again whenever it changes: a
  // one-shot probe on the resolved load would leave every region unpinged for
  // the rest of the page if this load failed and another component's retry
  // succeeded. Re-entrant by design -- enriching a region replaces the
  // catalogue and so re-fires this. `latencyProbes` is what stops it looping.
  watch(serverLocations, processServersLatency, { immediate: true });
  loadServerLocations();

  function seeNotification() {
    showNotifications.value = false;
    axios.post(`/notifications/${notifications.value[0].id}/see`);
  }

  function getLevelOneNotifications() {
    axios.get('/notifications?level=1')
      .then((response) => {
        if (response.data.length > 0) showNotifications.value = true;
        notifications.value = response.data;
      });
  }

  getLevelOneNotifications();

  function updateButtonColor() {
    const r = document.querySelector(':root');
    if (selectedGame.value === 'csgo') {
      r.style.setProperty('--primary-600', '#687cca');
      r.style.setProperty('--primary-500', '#556cc6');
    } else {
      r.style.setProperty('--primary-600', '#4E54C8');
      r.style.setProperty('--primary-500', '#474cba');
    }
  }

  function loadSelectedGame() {
    if (selectedGame.value) {
      store.commit('setSelectedGame', selectedGame.value);
    } else {
      store.commit('setSelectedGame', 'cs2');
      selectedGame.value = 'cs2';
    }
    updateButtonColor();
    axios.defaults.headers.common['X-GAME'] = store.getters.selectedGame;
  }

  function loadModInfo() {
    trackSharedLoad('mods', () => axios.get('mods').then(({ data }) => {
      if (auth.user()?.access_level > 1) {
        store.commit('setModInfo', data);
      } else {
        const filteredMods = data.mods.filter((obj) => obj.name !== 'Scrim');
        store.commit('setModInfo', { mods: filteredMods, tags: data.tags });
      }
    }));
  }

  function setActiveTeam(team) {
    // Keep the store, persisted id, and the axios X-TEAM-ID default header in
    // sync. After a cached boot the watcher re-runs this with fresh /me teams,
    // so requests must not keep scoping to a team the user no longer has.
    store.commit('setActiveTeam', team);
    localStorage.activeTeamId = team.id;
    axios.defaults.headers.common['X-TEAM-ID'] = team.id;
    activeTeam.value = team;
  }

  function loadActiveTeam() {
    const teams = auth.user()?.teams || [];
    if (teams.length) {
      const team = (localStorage.activeTeamId
        && teams.find((x) => String(x.id) === String(localStorage.activeTeamId))) || teams[0];
      setActiveTeam(team);
    }
    loadModInfo();
  }

  // A full reload, so every page, header and channel picks the new team up at once
  function switchTeam(team) {
    localStorage.activeTeamId = team.id;
    window.location.reload();
  }

  let matchmakingSubscription = null;
  let presenceSubscription = null;
  let cableConsumer = null;

  // $cable.connect() opens a fresh WebSocket every call, so the layout keeps one
  // consumer and hangs all of its subscriptions off it.
  function getCableConsumer() {
    if (!cableConsumer) cableConsumer = proxy.$cable.connect();
    return cableConsumer;
  }

  // Same gate as the sidebar item and MatchmakingBlock: without access, never fetch
  // party/lobby/invite state or open MatchmakingChannel.
  const canUseMatchmaking = () => !!auth.user()?.can_use_matchmaking;

  watch(() => [store.getters.matchmakingParty?.id, canUseMatchmaking()], ([partyId, allowed]) => {
    if (!partyId || !allowed || partyId === pingsReportedForPartyId) return;

    pingsReportedForPartyId = partyId;
    reportPings();
  }, { immediate: true });

  // Party plus the queue context (LAN parties, seat offers)
  const fetchParties = () => trackSharedLoad('party', () => axios.get('/matchmaking/parties').then(({ data }) => {
    store.commit('setMatchmakingParty', data.current || data.owned);
    store.commit('setMatchmakingContext', data);
  }));

  // The seat offer is computed on read, two minutes into a regular queue: poll while queued.
  let offerTimer = null;
  watch(() => store.getters.matchmakingParty?.status === 'queued', (queued) => {
    clearInterval(offerTimer);
    offerTimer = null;
    if (!queued || !canUseMatchmaking()) return;
    offerTimer = setInterval(fetchParties, 30_000);
  }, { immediate: true });

  function subscribeToMatchmaking() {
    // Also runs from the auth.user() watcher, for when the live /me lands after
    // setup and only then grants matchmaking access.
    if (matchmakingSubscription || !canUseMatchmaking()) return;

    fetchParties();
    axios
      .get('/matchmaking/lobby')
      .then(({ data }) => store.commit('setMatchmakingLobby', data))
      .catch(() => store.commit('clearMatchmakingLobby'));
    axios
      .get('/matchmaking/party_invites')
      .then(({ data }) => store.commit('setMatchmakingPartyInvites', data.pending))
      .catch(() => {});

    matchmakingSubscription = getCableConsumer().subscriptions.create({ channel: 'MatchmakingChannel' }, {
      received: (data) => {
        if (data.type === 'party_update') {
          if (data.party.status === 'queued' && store.getters.matchmakingParty?.status !== 'queued') {
            reportPings();
          }
          store.commit('setMatchmakingParty', data.party);
          // Party moved into a lobby — fetch it in case the lobby_update WS message was missed
          if (data.party.status === 'in_lobby') {
            axios.get('/matchmaking/lobby')
              .then(({ data: lobbyData }) => store.commit('setMatchmakingLobby', lobbyData))
              .catch(() => {});
          }
        } else if (data.type === 'lobby_update') {
          store.commit('setMatchmakingLobby', data.lobby);
        } else if (data.type === 'lan_party_room_update') {
          window.dispatchEvent(new CustomEvent('lan-party-room-update', { detail: data.lan_party_uuid }));
        } else if (data.type === 'party_invite_creation') {
          store.commit('addMatchmakingPartyInvite', data.party_invite);
        } else if (data.type === 'party_invite_removal') {
          // Cancelled, dropped when the party queued, or the party is gone: close the prompt
          store.commit('removeMatchmakingPartyInvite', data.party_invite_id);
        }
      },
    });
  }

  // Presence: subscribing marks this user online (the server heartbeats the
  // Redis key itself) and delivers live online/offline pushes about friends.
  // Friends aren't gated, so every user gets this.
  function subscribeToPresence() {
    if (presenceSubscription) return;

    store.commit('clearFriendPresence'); // session-persisted; drop stale overrides
    presenceSubscription = getCableConsumer().subscriptions.create({ channel: 'PresenceChannel' }, {
      received: (data) => {
        if (data.type === 'presence') {
          store.dispatch('receiveFriendPresence', { uuid: data.user_uuid, online: data.status === 'online' });
        }
      },
    });
  }

  onUnmounted(() => {
    clearInterval(offerTimer);
    matchmakingSubscription?.unsubscribe();
    matchmakingSubscription = null;
    presenceSubscription?.unsubscribe();
    presenceSubscription = null;
    cableConsumer?.disconnect();
    cableConsumer = null;
  });

  loadActiveTeam();
  loadSelectedGame();
  subscribeToMatchmaking();
  subscribeToPresence();

  // When we boot from cached /me, the live /me lands a moment later and
  // replaces the user object. Re-derive the team and matchmaking state from it.
  watch(
    () => auth.user(),
    (user) => {
      // vue-auth nulls the user on logout/401: nothing to re-derive mid-teardown.
      if (!user) return;
      if (user.teams) loadActiveTeam();
      // The cached /me we booted from may predate matchmaking access
      subscribeToMatchmaking();
    },
  );

  return {
    activeTeam,
    selectedGame,
    loadSelectedGame,
    switchTeam,
    notifications,
    showNotifications,
    seeNotification,
    activeLobby,
    matchmakingStatus,
    getCableConsumer,
  };
}
